import {
  ChangeDetectorRef,
  Directive,
  EmbeddedViewRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import {combineLatest, EMPTY, merge, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {exhaustMap, map, takeUntil, tap, shareReplay} from 'rxjs/operators';

import {AbstractRoleUpdateAwareProvider} from '../../requests/classes/role-update-aware-provider';
import {UserPermissionsService} from '../services/user-permissions.service';
import {EventEmitter} from '@angular/core';

/**
 * @usageNotes
 *
 * To remove from template a component inaccessible due to permissions use directive
 * on ng-template component
 * @example
 * <ng-template [appUserPermissionsV2]="'PERMISSION_NAME'" #scopePermissions="appUserPermissionsV2">
 *  <div *ngIf="scopePermissions.isAllowed$ | async">...</div>
 *  <div *appUserPermissionsV2="'PERMISSION_NAME'">...</div>
 * </ng-template>
 * ```
 * Content of ng-template will not be rendered
 */

@Directive({
  selector: '[appUserPermissionsV2]',
  exportAs: 'appUserPermissionsV2'
})
export class UserPermissionsV2Directive implements OnInit, OnDestroy {
  public isAllowed: boolean;

  private userPermissionsSubject = new ReplaySubject<string[]>(1);
  private ngUnsubscribe = new Subject<void>();

  private roleUpdates$: Observable<string[]>;
  isAllowed$: Observable<boolean>;

  constructor(
    private viewContainer: ViewContainerRef,
    private userPermissionsService: UserPermissionsService,
    private cd: ChangeDetectorRef,
    @Optional() private roleProvider: AbstractRoleUpdateAwareProvider,
    @Optional() private templateRef?: TemplateRef<any>
  ) {
    this.roleUpdates$ = combineLatest([
      this.userPermissionsSubject.asObservable(),
      merge(of(null), this.roleProvider?.roleUpdates$ || EMPTY)
    ]).pipe(
      map(([roles]) => roles),
      takeUntil(this.ngUnsubscribe)
    );

    this.isAllowed$ = merge(
      of(false),
      this.roleUpdates$.pipe(
        exhaustMap(permission => (permission ? this.userPermissionsService.isActionAvailable(permission, this.roleProvider) : of(true))),
        tap(() => {
          this.cd.detectChanges();
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true
        })
      )
    ).pipe(takeUntil(this.ngUnsubscribe));
  }

  @Input('appUserPermissionsV2') set appUserPermissions(permission: string | string[]) {
    const arrayPermission = [permission].flat();
    this.userPermissionsSubject.next(arrayPermission);
  }

  @Output() allowedChanged = new EventEmitter(true);
  private currentEmbeddedView: EmbeddedViewRef<any>;

  ngOnInit() {
    this.isAllowed$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: isAllowed => {
        if (this.templateRef) {
          this.updateContainerRef(isAllowed);
        }
        this.allowedChanged.emit(isAllowed);
      },
      complete() {
        if (this.templateRef) {
          this.viewContainer.clear();
          this.currentEmbeddedView = null;
        }
      }
    });
  }

  private async updateContainerRef(isAllowed) {
    if (isAllowed) {
      if (this.templateRef && !this.currentEmbeddedView) {
        this.currentEmbeddedView = this.viewContainer.createEmbeddedView(this.templateRef);
      }
      this.isAllowed = true;
    } else {
      if (this.templateRef) {
        this.viewContainer.clear();
        this.currentEmbeddedView = null;
      }
      this.isAllowed = false;
    }
    this.cd.markForCheck();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
